import { useState, useCallback, useEffect, RefObject } from 'react';
import { calculateOptimalPlacement, TPlacement } from '../utils/placementUtils';

export interface IBubbleInfo {
  ele: HTMLElement;
  text: string;
  placement: TPlacement;
}

export interface IUseBubblePlacementOptions {
  /** 预估气泡宽度 */
  bubbleWidth?: number;
  /** 预留边距 */
  margin?: number;
}

/**
 * 气泡位置管理 Hook
 * @param targetRef 目标元素引用
 * @param options 配置选项
 * @returns 气泡信息和相关方法
 */
export const useBubblePlacement = (
  targetRef: RefObject<HTMLElement>,
  options: IUseBubblePlacementOptions = {}
) => {
  const [bubbleInfo, setBubbleInfo] = useState<IBubbleInfo | null>(null);

  const {
    bubbleWidth = 314,
    margin = 20
  } = options;

  /**
   * 计算气泡的最佳位置
   */
  const calculatePlacement = useCallback((element: HTMLElement): TPlacement => 
    calculateOptimalPlacement(element, {
      bubbleWidth,
      margin
    }), [bubbleWidth, margin]);

  /**
   * 显示气泡
   */
  const showBubble = useCallback((text: string, targetElement?: HTMLElement) => {
    const element = targetElement || targetRef.current;
    if (!element) return;

    const placement = calculatePlacement(element);
    setBubbleInfo({
      ele: element,
      text,
      placement
    });
  }, [targetRef, calculatePlacement]);

  /**
   * 隐藏气泡
   */
  const hideBubble = useCallback(() => {
    setBubbleInfo(null);
  }, []);

  /**
   * 重新计算气泡位置（当窗口大小改变时）
   */
  const recalculatePlacement = useCallback(() => {
    if (bubbleInfo && targetRef.current) {
      const newPlacement = calculatePlacement(targetRef.current);
      if (newPlacement !== bubbleInfo.placement) {
        setBubbleInfo(prev => prev ? { ...prev, placement: newPlacement } : null);
      }
    }
  }, [bubbleInfo, targetRef, calculatePlacement]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      recalculatePlacement();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [recalculatePlacement]);

  return {
    bubbleInfo,
    showBubble,
    hideBubble,
    recalculatePlacement
  };
};
